import { Selector } from "react-redux";
import { createSelector } from "reselect";

import { bridge } from "@multiweb/reactCore/bridge";
import { IMenuItemData, MenuItemType } from "@multiweb/types/contracts";
import { IAppState, MutableTypedObject, TypedObject } from "@multiweb/types/react";
import { getFavoriteMenuItemIds } from "../favoriteItems";
import { getOpenedFormsHistoryInfo } from "../historyItems";
import { LOC_KEY_QUICK_ACCESS_MENU_FAVORITES, LOC_KEY_QUICK_ACCESS_MENU_HISTORY_ITEMS, LOC_KEY_QUICK_ACCESS_MENU_OPENED_ITEMS } from "../locKeys";
import { getFormInstanceBaseLabel, getOpenedFormsIds, getOrderedOpenedFormsInfo } from "../openedItems";
import {
    createGetFormNameMap,
    getAllowedRootFormItemIds,
    getFormItems,
    getProfiles,
    getTextCompareFn,
} from "../selectors";
import { SearchData, SearchResult, SearchResultType } from "./types";
import {
    createGroupHeaderResult,
    createSearchData,
    createSearchDataFilter,
    createSearchDataToResultMapper,
    getIdsForSubtree,
    sortAndFinalizeSearchGroup,
} from "./utils";

const { removeAccents } = bridge;

/**
 * Získá z globálního stavu podstrom pro vyhledávání.
 */
const getModel = (state: IAppState) => state.menu.search;

/**
 * Získá text pro vyhledávání formulářů.
 * @param state
 */
export const getSearchedText: Selector<IAppState, string> = (state) => getModel(state).searchedText;

/**
 * Získá text pro vyhledávání formulářů zbavený diakritiky a převedený na velká písmena.
 */
const getUpperCaseAccentsFreeSearchedText = createSelector(
    getSearchedText,
    (searchedText) => removeAccents(searchedText.toLocaleUpperCase()),
);

/**
 * Zjisti, zda je aktivní vyhledávání formulářových položek.
 * @param state
 */
export const isSearchActive: Selector<IAppState, boolean> = (state) => !!getSearchedText(state);

/**
 * Získá vyhledané výsledky. Výsledky nemusí odpovídat vyhledávanému textu, protože vyhledávání se spustí až ve chvíli, kdy uživatel přestane zadávat text.
 * Vyhledávání jako takové provádí selector getSearchedItems.
 */
export const getSearchResults: Selector<IAppState, readonly SearchResult[]> = (state) => getModel(state).searchResults;

/**
 * Získá všechny listové formuláře - odfiltruje položky formulářových submenu. Selector je cachovaný.
 * @param state
 */
export const getLeafForms = createSelector<IAppState, ReturnType<typeof getFormItems>, IMenuItemData[]>(
    getFormItems,
    (forms) => Object.values(forms).filter((form: IMenuItemData) => form.type === MenuItemType.form),
);

/**
 * Získá mapu lokalizovaných názvů pro listové formuláře. Selector je cachovaný.
 * @param state
 */
const getLeafFormsNameMap = createSelector(
    getLeafForms,
    (leafForms) => createGetFormNameMap(leafForms.map((form) => form.id)),
);

/**
 * Vytvoří datovou strukturu vhodnou pro vyhledávání formulářů. Obsahuje pouze listové formuláře. Submenu formulářů se nevyhledávají. Struktura je indexovaná
 * podle id položky, aby bylo možné ji znovupoužít při vyhledávání v různých kategoriích.
 * Struktura obsahuje:
 * id - Id formuláře.
 * text - Lokalizovaný název formuláře - vhodný pro řazení výsledků.
 * upperCaseAccentsFreeText - Lokalizovaný název formuláře převedený na velká písmena s odstraněnou diakritikou - vhodný pro case-insensitive vyhledávání.
 * upperCaseAccentsFreeFormCode - Kód formuláře převedený na velká písmena s odstraněnou diakritikou - vhodný pro case-insensitive vyhledávání.
 *
 * Selector je cachovaný. Přepočítává se při změně listových formulářů nebo jejich lokalizací.
 *
 * @param state
 * @return Vrací objekt obsahující objekty s výše popsanou strukturou indexované podle id.
 */
const getFormsForSearch = createSelector(
    getLeafForms,
    (state) => getLeafFormsNameMap(state)(state),
    (leafForms, leafFormsNameMap) => leafForms.reduce((result, { id, formCode }) => {
        result[id] = createSearchData(id, leafFormsNameMap[id], formCode, SearchResultType.form);
        return result;
    }, {} as MutableTypedObject<SearchData>) as TypedObject<SearchData>,
);

/**
 * Vytvoří datovou strukturu vhodnou pro vyhledávání profilů. Struktura je indexovaná podle id profilu. Struktura obsahuje:
 * id - Id profilu.
 * text - Název profilu - vhodný pro řazení výsledků.
 * upperCaseAccentsFreeTitle - Název profilu převedený na velká písmena s odstraněnou diakritikou - vhodný pro case-insensitive vyhledávání.
 * upperCaseAccentsFreeFormCode - Kód rodičovského formuláře převedený na velká písmena s odstraněnou diakritikou - vhodný pro case-insensitive vyhledávání.
 *
 * Selector je cachovaný. Přepočítává se při změně profilů.
 *
 * @param state
 * @return Vrací objekt obsahující objekty s výše popsanou strukturou indexované podle id profilu.
 */
const getProfilesForSearch = createSelector(
    getProfiles,
    (profiles) => Object.values(profiles).reduce((result, { id, title, formCode }) => ({
        ...result,
        [id]: createSearchData(id, title, formCode, SearchResultType.profile),
    }), {} as TypedObject<SearchData>),
);

/**
 * Cachovaný selector pro získání údajů sloužících k vyhledávání ve skupině otevřených formulářů.
 */
export const getOpenedFormsForSearch = createSelector(
    getOrderedOpenedFormsInfo,
    (state: IAppState) => getOpenedFormsBaseLabelMapSelector(state)(state),
    (openedForms, openedFormsBaseLabelMap) => openedForms.map(
        ({ formInstanceId, formCode }) => createSearchData(formInstanceId, openedFormsBaseLabelMap[formInstanceId], formCode, SearchResultType.formInstance),
    ),
);

/**
 * Získá selector pro získání objektu mapujícího id instance otevřeného formuláře na základ jeho popisku (číslování není součástí).
 * Nový selector se vytváří pouze v případě změny v otevřených formulářích.
 */
const getOpenedFormsBaseLabelMapSelector = createSelector(
    getOpenedFormsIds,
    (openedFormsInfo) => createGetOpenedFormsBaseLabelMap(openedFormsInfo),
);

/**
 * Vytvoří cachovaný selector pro získání objektu mapujícího id instance otevřeného formuláře na základ jeho popisku (číslování není součástí). Selector se přepočítává
 * při změně základu popisku libovolného ze zadaných otevřených formulářů.
 * @param openedFormsIds Otevřené formuláře, pro které se selector vytváří.
 */
const createGetOpenedFormsBaseLabelMap = (openedFormsIds: readonly string[]) => createSelector(
    openedFormsIds.map((formInstanceId) => (state: IAppState) => getFormInstanceBaseLabel(state, formInstanceId)),
    (...openedFormBaseLabels) => openedFormBaseLabels.reduce((result, baseLabel, index) => ({
        ...result,
        [openedFormsIds[index]]: baseLabel,
    }), {} as TypedObject<string | undefined>),
);

/**
 * Získá výsledky vyhledávání pro skupinu otevřených formulářů. Otevřené formuláře nepodléhají vyhledávání, viz task 31650.
 * Výsledky se však i tak vracejí přes tento selector, aby se zobrazily jednotně ve virtualizovaném listu s ostatními vyhledanými položkami.
 */
export const getSearchedOpenedForms = createSelector(
    getOpenedFormsForSearch,
    (openedFormsForSearch) => {
        const openedFormsSearchResults: SearchResult[] = openedFormsForSearch
            .map(createSearchDataToResultMapper(LOC_KEY_QUICK_ACCESS_MENU_OPENED_ITEMS));

        return [
            createGroupHeaderResult(LOC_KEY_QUICK_ACCESS_MENU_OPENED_ITEMS),
            ...openedFormsSearchResults,
        ];
    },
);

/**
 * Získá výsledky vyhledávání pro skupinu historicky otevřených formulářů.
 */
export const getSearchedHistoricForms = createSelector(
    getUpperCaseAccentsFreeSearchedText,
    getOpenedFormsHistoryInfo,
    getFormsForSearch,
    getProfilesForSearch,
    getTextCompareFn,
    (upperCaseAccentsFreeSearchedText, openedFormsHistoryInfo, formsForSearch, profilesForSearch, textCompareFn) => {
        const searchResults: SearchResult[] = openedFormsHistoryInfo
            .map(({ profileId, menuItemId }) => profileId ? profilesForSearch[profileId] : formsForSearch[menuItemId])
            .filter(createSearchDataFilter(upperCaseAccentsFreeSearchedText))
            .map(createSearchDataToResultMapper(LOC_KEY_QUICK_ACCESS_MENU_HISTORY_ITEMS));

        return sortAndFinalizeSearchGroup(LOC_KEY_QUICK_ACCESS_MENU_HISTORY_ITEMS, searchResults, upperCaseAccentsFreeSearchedText, textCompareFn);
    },
);

/**
 * Získá výsledky vyhledávání pro skupinu oblíbených formulářů.
 */
export const getSearchedFavoriteForms = createSelector(
    getUpperCaseAccentsFreeSearchedText,
    getFavoriteMenuItemIds,
    getFormsForSearch,
    getTextCompareFn,
    (upperCaseAccentsFreeSearchedText, favoriteFormIds, formsForSearch, textCompareFn) => {
        const searchResults: SearchResult[] = favoriteFormIds
            .map((formItemId) => formsForSearch[formItemId.formId])
            .filter(createSearchDataFilter(upperCaseAccentsFreeSearchedText))
            .map(createSearchDataToResultMapper(LOC_KEY_QUICK_ACCESS_MENU_FAVORITES));

        return sortAndFinalizeSearchGroup(LOC_KEY_QUICK_ACCESS_MENU_FAVORITES, searchResults, upperCaseAccentsFreeSearchedText, textCompareFn);
    },
);

/**
 * Získá skupiny kořenových formulářů pro vyhledávání včetně ids všech formulářů a profilů, které do dané skupiny patří.
 * Obsažená ids jsou unikátní v rámci skupiny.
 */
export const getFormSearchGroups = createSelector(
    getAllowedRootFormItemIds,
    getFormItems,
    (rootItemsIds, formItems) => rootItemsIds.map((rootItemId) => {
        const { locKey } = formItems[rootItemId];
        const { formItemIds, profileIds } = getIdsForSubtree(rootItemId, formItems);
        return {
            locKey,
            formItemIds: [...new Set(formItemIds)],
            profileIds: [...new Set(profileIds)],
        };
    }),
);

/**
 * Získá výsledky vyhledávání pro skupiny kořenových formulářů.
 */
export const getSearchedForms = createSelector(
    getUpperCaseAccentsFreeSearchedText,
    getFormSearchGroups,
    getFormsForSearch,
    getProfilesForSearch,
    getTextCompareFn,
    (upperCaseAccentsFreeSearchedText, formSearchGroups, formsForSearch, profilesForSearch, textCompareFn) => {
        const searchDataFilter = createSearchDataFilter(upperCaseAccentsFreeSearchedText);
        return formSearchGroups
            .map(({ locKey, formItemIds, profileIds }) => {
                const searchDataToResultMapper = createSearchDataToResultMapper(locKey);
                const searchedForms: SearchResult[] = formItemIds
                    .map((formItemId) => formsForSearch[formItemId])
                    .filter(searchDataFilter)
                    .map(searchDataToResultMapper);
                const searchedProfiles: SearchResult[] = profileIds
                    .map((profileId) => profilesForSearch[profileId])
                    .filter(searchDataFilter)
                    .map(searchDataToResultMapper);

                return {
                    locKey,
                    searchResults: searchedForms.concat(searchedProfiles),
                };
            })
            .reduce((result, { locKey, searchResults }) => ([
                ...result,
                ...sortAndFinalizeSearchGroup(locKey, searchResults, upperCaseAccentsFreeSearchedText, textCompareFn),
            ]), [] as SearchResult[]);
    },
);

/**
 * Získá kompletní výsledky vyhledávání spojením výsledků z jednotlivých skupin.
 */
export const getSearchedItems = createSelector(
    getSearchedOpenedForms,
    getSearchedHistoricForms,
    getSearchedFavoriteForms,
    getSearchedForms,
    (searchedOpenedForms, searchedHistoricForms, searchedFavoriteForms, searchedForms) => [
        ...searchedOpenedForms,
        ...searchedHistoricForms,
        ...searchedFavoriteForms,
        ...searchedForms,
    ],
);
